import { useId, useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { GPTModelItem } from "@/api/models"

import { ReasoningModePill } from "@/features/chat/components/reasoning-mode-pill"

import {
  createWorkflowModelChange,
  normalizeWorkflowModelId,
  normalizeWorkflowReasoningEffort,
  resolveWorkflowEffortOverride,
  resolveWorkflowModel,
  type WorkflowModelSettingsPatch,
} from "../utils/workflow-model-picker-utils"

type WorkflowModelPickerProps = {
  availableModels: GPTModelItem[]
  modelId: unknown
  reasoningEffort: unknown
  allowMaxEffort?: boolean
  hasCatalogError?: boolean
  isCatalogLoading?: boolean
  onChange: (patch: WorkflowModelSettingsPatch) => void
}

export function WorkflowModelPicker({
  availableModels,
  modelId,
  reasoningEffort,
  allowMaxEffort = true,
  hasCatalogError = false,
  isCatalogLoading = false,
  onChange,
}: WorkflowModelPickerProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })
  const validationId = useId()
  // Azure Chat Completions rejects `max` even for GPT-5.6, while the Agent
  // node's Responses path supports it. Keep one visual picker but expose only
  // efforts the node's actual API surface can honor.
  const pickerModels = useMemo(() => {
    if (allowMaxEffort) return availableModels
    return availableModels.map((model) => {
      const supportedEfforts = (model.supported_efforts ?? []).filter((effort) => effort !== "max")
      if (supportedEfforts.length === (model.supported_efforts ?? []).length) return model
      return {
        ...model,
        supported_efforts: supportedEfforts,
        default_effort: supportedEfforts.includes(model.default_effort ?? "")
          ? model.default_effort
          : (supportedEfforts.at(-1) ?? ""),
      }
    })
  }, [allowMaxEffort, availableModels])

  const persistedModelId = normalizeWorkflowModelId(modelId)
  const selectedModel = resolveWorkflowModel(pickerModels, persistedModelId)
  const catalogReady = !isCatalogLoading && !hasCatalogError
  const hasStaleModel = Boolean(catalogReady && persistedModelId && !selectedModel)
  const persistedEffort = normalizeWorkflowReasoningEffort(reasoningEffort)
  const hasExplicitEffort =
    reasoningEffort !== undefined && reasoningEffort !== null && reasoningEffort !== ""
  const hasStaleEffort = Boolean(
    catalogReady &&
    !hasStaleModel &&
    hasExplicitEffort &&
    (!persistedEffort || !selectedModel?.supported_efforts?.includes(persistedEffort))
  )
  const effortOverride = resolveWorkflowEffortOverride(selectedModel, reasoningEffort)
  const invalid = hasCatalogError || hasStaleModel || hasStaleEffort
  let describedBy: string | undefined
  if (hasCatalogError) describedBy = `${validationId}-catalog`
  else if (hasStaleModel) describedBy = `${validationId}-model`
  else if (hasStaleEffort) describedBy = `${validationId}-effort`

  let triggerLabelOverride: string | undefined
  if (isCatalogLoading) {
    triggerLabelOverride = t("modelPicker.loading", "Loading models…")
  } else if (hasCatalogError) {
    triggerLabelOverride = t("modelPicker.loadFailed", "Models unavailable")
  } else if (invalid) {
    triggerLabelOverride = t("modelPicker.selectModel", "Select model")
  }

  return (
    <div className="nodrag min-w-0 space-y-0.5">
      <ReasoningModePill
        ariaDescribedBy={describedBy}
        availableModels={pickerModels}
        contentClassName="z-[100]"
        disabled={isCatalogLoading || pickerModels.length === 0}
        effortOverride={effortOverride}
        invalid={invalid}
        modelOverride={persistedModelId ?? null}
        selectedModel={selectedModel}
        showShortcutHints={false}
        triggerLabelOverride={triggerLabelOverride}
        onEffortOverrideChange={(effort) => onChange({ reasoningEffort: effort ?? undefined })}
        onModelPick={(nextModelId) =>
          onChange(createWorkflowModelChange(pickerModels, nextModelId, reasoningEffort))
        }
      />
      {hasCatalogError && (
        <p className="text-destructive text-[10px] leading-tight" id={`${validationId}-catalog`}>
          {t("modelPicker.catalogUnavailable", "Models could not be loaded. Try again later.")}
        </p>
      )}
      {hasStaleModel && (
        <p className="text-destructive text-[10px] leading-tight" id={`${validationId}-model`}>
          {t(
            "modelPicker.modelUnavailable",
            "The saved model is no longer available. Select a new model."
          )}
        </p>
      )}
      {hasStaleEffort && (
        <p className="text-destructive text-[10px] leading-tight" id={`${validationId}-effort`}>
          {t(
            "modelPicker.effortUnavailable",
            "The saved reasoning level is not supported here. Select a new level."
          )}
        </p>
      )}
    </div>
  )
}
